import React, { createContext, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkSlug from 'remark-slug';

import { DocsArticle } from '../Docs.styles';
import DocsCodeBlock from './DocsCodeBlock';

export interface DocsMarkdownProps {
  markdown: string;
  /** Passed to runnable code blocks as the new sketch's name. */
  pageTitle?: string;
}

/** `language-js-runnable` → `js-runnable`. */
export const languageOf = (className?: string) =>
  /language-([\w-]+)/.exec(className || '')?.[1];

/**
 * The renderers live at module scope so react-markdown sees the same component types on every
 * render (a fresh type per render would remount every code block, losing its "Copied" state).
 * That leaves them no way to be handed the page title as a prop, hence this context.
 */
const PageTitleContext = createContext<string | undefined>(undefined);

interface CodeProps {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const Code = ({ inline, className, children }: CodeProps) => {
  const pageTitle = useContext(PageTitleContext);
  if (inline) return <code className={className}>{children}</code>;
  return (
    <DocsCodeBlock
      code={String(children).replace(/\n$/, '')}
      language={languageOf(className)}
      snippetTitle={pageTitle}
    />
  );
};

// DocsCodeBlock brings its own <pre>; without this react-markdown nests it inside another.
const Pre = ({ children }: { children?: React.ReactNode }) => <>{children}</>;

const RENDERERS = { code: Code, pre: Pre };

/**
 * Renders a docs page. `remark-slug` gives every heading an id so in-page anchors (the
 * reference "Related" links point at them) resolve.
 *
 * Code fences are handed to DocsCodeBlock, which adds the Copy / "Open in editor" toolbar.
 * There is deliberately no syntax highlighter: the editor ships none today, and pulling one in
 * for the docs would add a dependency (and bundle weight) for decoration only.
 */
const DocsMarkdown = ({ markdown, pageTitle }: DocsMarkdownProps) => (
  <PageTitleContext.Provider value={pageTitle}>
    <DocsArticle>
      <ReactMarkdown remarkPlugins={[remarkSlug]} components={RENDERERS}>
        {markdown}
      </ReactMarkdown>
    </DocsArticle>
  </PageTitleContext.Provider>
);

export default DocsMarkdown;
